import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, countryFlag } from "../lib/utils";
import { Eye, X, Package, Funnel, CheckCircle, Warning, Calendar, MapPin, Storefront, CaretRight, Lightning, Trash } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import BottomSheet from "../components/BottomSheet";
import OrderDetailBody from "../components/OrderDetailBody";
import DangerConfirm from "../components/DangerConfirm";
import { toast } from "sonner";

const STATUS_LABELS = {
  charged: { label: "Pagato", cls: "tf-status-success" },
  blocked: { label: "Bloccato", cls: "tf-status-error" },
  error: { label: "Errore", cls: "tf-status-error" },
  unmatched: { label: "Non abbinato", cls: "tf-status-warning" },
};

export default function AdminOrders() {
  const { t, lang } = useI18n();
  const [sp] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState(sp.get("status") || "all");
  const [clientFilter, setClientFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data));
    loadOrders();
  }, []);

  const filtered = orders.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (clientFilter && o.client_id !== clientFilter) return false;
    return true;
  });

  const clientName = (id) => clients.find((c) => c.id === id)?.company_name || "—";
  const podCost = (o) => (o.cost?.items_total || 0) - (o.cost?.picking_fee || 0);
  const statusFor = (o) => (o.status === "blocked" && o.block_reason === "unknown_sku"
    ? { label: "SKU ignoto", cls: "tf-status-error" }
    : (STATUS_LABELS[o.status] || { label: o.status, cls: "tf-status-neutral" }));
  const unknownSkuOrders = orders.filter((o) => o.status === "blocked" && o.block_reason === "unknown_sku");

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <PageHeader
        eyebrow="Ordini"
        title="Feed ordini"
        subtitle="Tutti gli ordini importati da Myze, con stato, destinazione e composizione di costo."
        chip="tf-chip-lime"
        icon={Package}
      />

      {unknownSkuOrders.length > 0 && (
        <div className="tf-card p-4 border-2 border-[var(--tf-error)] bg-[#FBDDE1]/40" data-testid="unknown-sku-alert-banner">
          <div className="flex items-center gap-2 font-display font-bold text-sm text-[var(--tf-error)]">
            <Warning size={18} weight="fill" /> {unknownSkuOrders.length} ordin{unknownSkuOrders.length === 1 ? "e bloccato" : "i bloccati"} — SKU ignoto
          </div>
          <p className="text-xs mt-1.5 text-[var(--tf-ink)]">
            Alcuni codici prodotto ricevuti da Myze non sono presenti nel listino cliente. Mappa il prezzo per sbloccare automaticamente l'ordine.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {unknownSkuOrders.slice(0, 4).map((o) => (
              <Link
                key={o.id}
                to={`/admin/pricelists?client=${o.client_id}&new_code=${(o.unknown_skus || [])[0] || ""}`}
                className="tf-btn tf-btn-primary !py-1.5 !px-3 text-xs"
                data-testid={`map-sku-link-${o.id}`}
              >
                Mappa {(o.unknown_skus || [])[0]}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="tf-card p-4 flex flex-wrap gap-3 items-end">
        <div className="tf-chip tf-chip-cream !w-10 !h-10 !rounded-xl">
          <Funnel size={16} weight="fill" />
        </div>
        <div>
          <label className="tf-label block mb-1">{t("status")}</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="tf-input" data-testid="filter-status">
            <option value="all">{t("all")}</option>
            <option value="charged">Pagato</option>
            <option value="blocked">Bloccato</option>
            <option value="error">Errore</option>
            <option value="unmatched">Non abbinato</option>
          </select>
        </div>
        <div>
          <label className="tf-label block mb-1">Shop</label>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="tf-input" data-testid="filter-client">
            <option value="">{t("all")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div className="ml-auto text-xs text-[var(--tf-muted)]">
          Mostro <b className="text-[var(--tf-ink)] tf-numeric">{filtered.length}</b> di <b className="text-[var(--tf-ink)] tf-numeric">{orders.length}</b>
        </div>
      </div>

      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Ordine</th>
                <th>ID</th>
                <th>Shop</th>
                <th>Stato</th>
                <th>Destinazione</th>
                <th className="text-right">€ POD</th>
                <th className="text-right">€ Spedizione</th>
                <th className="text-right">€ Picking</th>
                <th className="text-right">Totale</th>
                <th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center text-[var(--tf-muted)] py-10">{t("empty")}</td></tr>
              )}
              {filtered.map((o) => {
                const st = statusFor(o);
                return (
                  <tr key={o.id} data-testid={`order-${o.id}`}>
                    <td className="text-xs font-mono whitespace-nowrap">{fmtDate(o.myze_created_at || o.created_at, lang)}</td>
                    <td className="font-mono text-xs">{o.origin_id || "—"}</td>
                    <td className="font-mono text-xs">{o.external_order_number || o.myze_order_id || "—"}</td>
                    <td>
                      <div className="font-medium">{o.shop_name || clientName(o.client_id)}</div>
                      {o.shop_name && (
                        <div className="text-[10px] text-[var(--tf-muted)] mt-0.5">{clientName(o.client_id)}</div>
                      )}
                    </td>
                    <td><span className={`tf-status-pill ${st.cls}`}>{st.label}</span></td>
                    <td className="font-mono text-xs">{o.shipping_country ? `${countryFlag(o.shipping_country)} ${o.shipping_country}` : "—"}</td>
                    <td className="text-right tf-numeric">{fmtEur(podCost(o))}</td>
                    <td className="text-right tf-numeric">{fmtEur(o.cost?.shipping || 0)}</td>
                    <td className="text-right tf-numeric">{fmtEur(o.cost?.picking_fee || 0)}</td>
                    <td className="text-right tf-numeric font-bold">{fmtEur(o.cost?.total || 0)}</td>
                    <td>
                      <button
                        onClick={() => setSelected(o)}
                        className="tf-btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                        data-testid={`detail-${o.id}`}
                      >
                        <Eye size={14} weight="bold" /> Dettaglio
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="admin-orders-mlist">
        {filtered.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <Package size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {t("empty")}
          </div>
        )}
        {filtered.map((o) => {
          const st = statusFor(o);
          const isBlocked = o.status === "blocked";
          const isError = o.status === "error" || o.status === "unmatched";
          const iconBg = isBlocked ? "#FBDDE1" : isError ? "#FFE7D3" : "#ECFCC9";
          const iconColor = isBlocked ? "#A11534" : isError ? "#A54B0F" : "#3E5A0A";
          const Icon = isBlocked || isError ? Warning : CheckCircle;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setSelected(o)}
              className="tf-mrow text-left"
              data-testid={`admin-order-card-${o.id}`}
            >
              <div className="tf-mrow-icon" style={{ background: iconBg, color: iconColor }}>
                <Icon size={20} weight="fill" />
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title truncate">{o.origin_id || o.external_order_number || o.myze_order_id || "—"}</div>
                <div className="tf-mrow-sub flex items-center gap-1.5 truncate">
                  <Storefront size={11} weight="bold" />
                  <span className="truncate">{o.shop_name || clientName(o.client_id)}</span>
                </div>
                <div className="tf-mrow-sub flex items-center gap-1.5 mt-0.5">
                  <Calendar size={11} weight="bold" />
                  <span>{fmtDate(o.myze_created_at || o.created_at, lang)}</span>
                  <span className="opacity-40">·</span>
                  <MapPin size={11} weight="bold" />
                  <span>{o.shipping_country ? `${countryFlag(o.shipping_country)} ${o.shipping_country}` : "—"}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="tf-mrow-value tf-numeric">{fmtEur(o.cost?.total || 0)}</div>
                <span className={`tf-status-pill ${st.cls} text-[9px]`}>{st.label}</span>
              </div>
              <CaretRight size={14} weight="bold" className="tf-mrow-chev" />
            </button>
          );
        })}
      </div>

      {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} lang={lang} shopName={selected.shop_name || clientName(selected.client_id)} onChanged={loadOrders} />}
    </div>
  );
}

function OrderDetailModal({ order, onClose, lang, shopName, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const isBlocked = order.status === "blocked";

  const forceUnblock = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/force-unblock`);
      toast.success("Sbloccato");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/orders/${order.id}`);
      if (data.refunded > 0) toast.success(`Eliminato · rimborsati €${data.refunded.toFixed(2)}`);
      else toast.success("Eliminato");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const shopChip = shopName ? (
    <div>
      <div className="tf-label">Cliente</div>
      <div className="font-display font-bold text-base mt-1">{shopName}</div>
    </div>
  ) : null;

  return (
    <BottomSheet
      open={!!order}
      onClose={onClose}
      subtitle="Dettaglio ordine"
      title={order.origin_id || order.external_order_number || order.myze_order_id}
      testId="order-detail-modal"
      footer={
        <div className="flex gap-2 flex-wrap" data-testid="admin-order-actions">
          {isBlocked && (
            <button
              onClick={forceUnblock}
              disabled={busy}
              className="flex-1 min-w-[140px] rounded-2xl bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] text-[var(--tf-ink)] font-bold text-sm flex items-center justify-center gap-2 transition disabled:opacity-50"
              data-testid="btn-force-unblock"
              style={{ padding: "14px 16px" }}
            >
              <Lightning size={16} weight="fill" /> Sblocca (senza addebito)
            </button>
          )}
          <button
            onClick={() => setAskDelete(true)}
            disabled={busy}
            className="flex-1 min-w-[120px] rounded-2xl border border-[var(--tf-border)] text-[var(--tf-error)] hover:bg-[#FBDDE1] hover:border-[var(--tf-error)] font-bold text-sm flex items-center justify-center gap-2 transition disabled:opacity-50"
            data-testid="btn-delete-order"
            style={{ padding: "14px 16px" }}
          >
            <Trash size={16} weight="bold" /> Elimina
          </button>
        </div>
      }
    >
      <OrderDetailBody
        order={order}
        lang={lang}
        extraChip={shopChip}
        mapSkuUrl={order.block_reason === "unknown_sku" ? `/admin/pricelists?client=${order.client_id}&new_code=${(order.unknown_skus || [])[0] || ""}` : null}
      />
      <DangerConfirm
        open={askDelete}
        onClose={() => setAskDelete(false)}
        onConfirm={async () => { await doDelete(); setAskDelete(false); }}
        title="Eliminare questo ordine?"
        subtitle={order.origin_id || order.external_order_number || order.myze_order_id}
        description={order.status === "charged"
          ? "Se già pagato, il wallet verrà rimborsato automaticamente della cifra totale (imponibile + IVA)."
          : "L'ordine verrà rimosso definitivamente dal sistema."}
        confirmLabel="Elimina ordine"
        verifyWord="ELIMINA"
        testId="delete-order-confirm"
      />
    </BottomSheet>
  );
}
